/**
 * TAS Command — Sistema Digitale di Gestione Operazioni SAR
 *
 * Copyright (c) 2026 Matteo MESSINA, Alfonso CECERE
 * Tutti i diritti riservati.
 *
 * Sviluppato dagli autori a titolo personale, al di fuori
 * dell'orario di servizio e senza utilizzo di risorse o
 * strumentazione dell'amministrazione di appartenenza.
 *
 * Qualsiasi riproduzione, distribuzione o utilizzo non
 * autorizzato è vietato ai sensi della L. 633/1941.
 *
 * Autori:
 *   Matteo MESSINA  — VE, Comando VVF Benevento, Servizio TAS
 *   Alfonso CECERE  — VE, Comando VVF Benevento, Servizio TAS
 */

/**
 * connectivity.js — Rilevamento robusto della connettività (raggiungibilità server)
 *
 * PERCHÉ ESISTE: `navigator.onLine` è un segnale debole. È `false` solo quando il
 * browser sa che NON c'è alcuna interfaccia di rete. Resta `true` quando l'interfaccia
 * è su ma il server è irraggiungibile (uplink morto, captive portal, DNS giù, backend
 * down, segnale debole). Alcuni browser desktop lo ritornano SEMPRE `true`. Affidarsi
 * solo a esso lasciava l'app in uno stato "online" fittizio: niente banner offline,
 * niente accodamento delle scritture → app "non funzionante offline-first".
 *
 * COSA FA: combina `navigator.onLine` (necessario ma non sufficiente) con un PROBE
 * ATTIVO verso l'endpoint healthcheck `/api/`. Lo stato `online` è vero solo se il
 * browser ha rete E l'ultimo probe ha ricevuto una risposta HTTP dal server.
 *   - browser dice offline           → offline immediato (nessun probe)
 *   - probe riceve QUALSIASI risposta → online (anche 4xx/5xx = server raggiungibile)
 *   - probe va in timeout / errore   → offline
 *
 * Espone uno stato singleton osservabile (`subscribe`) e getter sincroni
 * (`isOnline`/`isOffline`) usati ovunque al posto di `navigator.onLine`.
 */
import { useEffect, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
// Endpoint healthcheck: GET /api/ → 200 senza auth (operazioni.root). Qualsiasi
// risposta HTTP, anche d'errore, prova che il server è raggiungibile.
// ⚠️ Il parametro `_probe` è DELIBERATO: rende l'URL unico a ogni giro e segnala
// al Service Worker (route #4) di NON gestire/servire dalla cache questa
// richiesta. Senza, offline il SW restituirebbe una risposta `/api/` cachata
// (NetworkFirst, 200) e il probe crederebbe di essere online. Va combinato con
// l'esclusione `_probe` nel service-worker.js.
const PROBE_URL = `${BACKEND_URL}/api/`;
const PROBE_TIMEOUT_MS = 6000;
const INTERVAL_ONLINE_MS = 20000; // creduto online: ricontrollo leggero

// Cadenza del probe mentre siamo offline: a SCALA, non fissa.
// Perché: fuori copertura il probe non trova nulla per definizione, e insistere ogni
// 5s per ore tiene sveglia la radio — in montagna, con l'operazione in corso, la
// batteria è una risorsa operativa. Nei primi ~10s si controlla fitto (rientro in
// copertura camminando, ripristino di un ponte radio), poi si allarga.
// Non è un compromesso sulla reattività: ogni segnale che il ripristino sia
// PLAUSIBILE — evento `online` del browser, ritorno in primo piano, azione
// dell'utente, richiesta fallita — riporta la scala al gradino più fitto.
const OFFLINE_LADDER_MS = [5000, 15000, 60000];
// Probe falliti consecutivi entro ciascun gradino prima di passare al successivo.
const OFFLINE_LADDER_STEP_AFTER = [2, 4];

let _online = navigator.onLine; // stato corrente di raggiungibilità creduto
let _probing = false;
let _timer = null;
let _timerMs = null;       // cadenza attualmente programmata (evita rescheduling inutili)
let _offlineStreak = 0;    // probe falliti consecutivi: indice sulla scala
const listeners = new Set();

/** Cadenza corrente: fissa se online, a scala crescente se offline. */
function intervalCorrente() {
  if (_online) return INTERVAL_ONLINE_MS;
  let gradino = 0;
  let soglia = 0;
  for (let i = 0; i < OFFLINE_LADDER_STEP_AFTER.length; i++) {
    soglia += OFFLINE_LADDER_STEP_AFTER[i];
    if (_offlineStreak >= soglia) gradino = i + 1;
  }
  return OFFLINE_LADDER_MS[Math.min(gradino, OFFLINE_LADDER_MS.length - 1)];
}

function emit() {
  for (const fn of listeners) {
    try { fn(_online); } catch { /* listener non deve rompere il giro */ }
  }
}

// Il Service Worker non ha un modo affidabile di sapere se il server e'
// raggiungibile (`navigator.onLine` mente pure li'): glielo diciamo noi, che
// siamo l'unica autorita' in materia. Gli serve per decidere quanto aspettare la
// rete prima di servire una risposta dalla cache (route API).
function notificaServiceWorker() {
  try {
    navigator.serviceWorker?.ready
      ?.then((reg) => reg.active?.postMessage({ type: 'TAS_CONNETTIVITA', online: _online }))
      ?.catch(() => {});
  } catch { /* browser senza SW: nulla da notificare */ }
}

function setOnline(val) {
  if (_online === val) return;
  _online = val;
  notificaServiceWorker();
  if (val) _offlineStreak = 0;  // tornati online: la scala riparte da capo
  schedule(); // cambia cadenza in base al nuovo stato
  emit();
}

/**
 * Riporta la scala al gradino più fitto e, se siamo offline, verifica subito.
 * Da chiamare quando qualcosa rende PLAUSIBILE un ripristino: l'utente torna
 * sull'app, tocca lo schermo, una richiesta fallisce (quindi sta lavorando), il
 * browser segnala che la rete è tornata. Senza questo, dopo un'ora fuori copertura
 * si resterebbe a controllare una volta al minuto anche col campo tornato pieno.
 */
export function wake() {
  const eraRilassato = _offlineStreak > 0;
  _offlineStreak = 0;
  if (eraRilassato) schedule();
  if (!_online) probe();
}

/** Stato corrente: true se il server è raggiungibile. */
export function isOnline() { return _online; }
/** Comodo inverso: true se offline. Sostituisce `!navigator.onLine`. */
export function isOffline() { return !_online; }

/**
 * Registra un listener notificato a ogni transizione online↔offline.
 * @param {(online:boolean)=>void} fn
 * @returns {()=>void} funzione di unsubscribe
 */
export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Esegue un probe attivo verso il server e aggiorna lo stato.
 * @returns {Promise<boolean>} true se raggiungibile
 */
export async function probe() {
  // Il browser sa che non c'è rete del tutto → fidati, niente chiamata inutile.
  // Conta comunque come tentativo fallito: fa salire la scala, così a dispositivo
  // in aereo o senza SIM non si resta a girare al ritmo più fitto.
  if (!navigator.onLine) {
    _offlineStreak++;
    setOnline(false);
    schedule();
    return false;
  }
  if (_probing) return _online;
  // Il Service Worker viene spento e riavviato dal browser di continuo e perde
  // lo stato: gli ripetiamo il verdetto a ogni probe, non solo ai cambi.
  notificaServiceWorker();
  _probing = true;
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), PROBE_TIMEOUT_MS);
  try {
    // `no-store` (cache HTTP) + `_probe=<ts>` univoco: quest'ultimo esce dalla
    // route di cache del Service Worker (vedi service-worker.js), così il probe
    // colpisce SEMPRE la rete reale e non una 200 stale servita dal SW col
    // server irraggiungibile.
    // `no-cors`: ci basta sapere se tornano byte dalla rete, non leggere il corpo
    // (risposta opaca) → niente dipendenza dagli header CORS del root /api/.
    await fetch(`${PROBE_URL}?_probe=${Date.now()}`, {
      method: 'GET',
      cache: 'no-store',
      mode: 'no-cors',
      signal: ctrl.signal,
    });
    _offlineStreak = 0;
    setOnline(true);
    return true;
  } catch {
    _offlineStreak++;
    setOnline(false);
    return false;
  } finally {
    clearTimeout(t);
    _probing = false;
    schedule();   // riallinea la cadenza al gradino raggiunto
  }
}

function schedule() {
  const iv = intervalCorrente();
  if (_timer && _timerMs === iv) return;   // già alla cadenza giusta: non riavviare
  if (_timer) clearInterval(_timer);
  _timerMs = iv;
  _timer = setInterval(() => {
    // Niente probe a tab nascosta (batteria/dati): visibilitychange ne lancia uno
    // appena la pagina torna in primo piano.
    if (document.visibilityState === 'visible') probe();
  }, iv);
}

/**
 * Da chiamare quando una richiesta fallisce con errore di RETE (nessuna risposta
 * HTTP): non fidarsi dello stato ottimistico, verifica subito con un probe.
 */
export function reportNetworkError() { wake(); }

/**
 * Da chiamare quando una richiesta va a buon fine mentre credevamo di essere
 * offline. NON promuove direttamente a online: una 200 può arrivare dalla cache
 * del Service Worker anche col server irraggiungibile. Lancia invece un probe
 * (che bypassa il SW) per VERIFICARE la raggiungibilità reale.
 */
export function reportNetworkSuccess() { if (!_online) probe(); }

// ── Wiring eventi browser ────────────────────────────────────────────────────
window.addEventListener('online', () => { wake(); });
window.addEventListener('offline', () => { setOnline(false); });
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') wake();
});

// Tocco/tasto dell'utente: se sta operando, vale la pena tornare a controllare
// fitto — è il momento in cui il ripristino della rete conta di più. Throttlato,
// listener passivi: non deve pesare sull'interazione.
let _ultimoWakeUtente = 0;
const wakeDaUtente = () => {
  if (_online) return;                                  // online: la cadenza è già quella giusta
  const ora = Date.now();
  if (ora - _ultimoWakeUtente < 5000) return;           // al massimo una volta ogni 5s
  _ultimoWakeUtente = ora;
  wake();
};
window.addEventListener('pointerdown', wakeDaUtente, { passive: true });
window.addEventListener('keydown', wakeDaUtente, { passive: true });

schedule();
probe(); // probe iniziale al caricamento del modulo

/**
 * Hook React: restituisce `true` se online, ri-renderizzando alle transizioni.
 * @returns {boolean}
 */
export function useConnectivity() {
  const [online, setStateOnline] = useState(isOnline());
  useEffect(() => subscribe(setStateOnline), []);
  return online;
}
